import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { StickyTable, Row, Cell } from 'experium-react-sticky-table';
import Translate from 'react-translate-component';
import cx from 'classnames';
import { propOr, isEmpty, compose, slice, filter, propEq, length } from 'ramda';

import { DEFAULT_COLUMN_WIDTH } from 'constants/defaultColumns';
import { mapIndexed } from 'utils/ramdaAdditions';
import { getTableColumns } from 'helpers/settingsHelpers';
import ErrorHTML from 'components/common/form/ErrorHTML';

import TableContentCell from './TableContentCell';
import TableHeaderCell from './TableHeaderCell';
import TableLabel, { TableLabelWithRoute } from './TableLabel';

import withTableRowsData from './wrappers/withTableRowsData';
import withTableFitDimensions from './wrappers/withTableFitDimensions';
import withTablePagination from './wrappers/withTablePagination';

const getLockedCount = (lockedColumnsCount, fields = []) => compose(
    length,
    filter(propEq('value', true)),
    slice(0, lockedColumnsCount)
)(fields);

@withTableFitDimensions
@withTablePagination
@withTableRowsData
export default class TableComponent extends Component {
    static propTypes = {
        //props
        defaultColumns: PropTypes.array.isRequired,
        getTemplate: PropTypes.func.isRequired,
        headerTranslatePrefix: PropTypes.string.isRequired,
        cellHandlers: PropTypes.object,
        handleColumnResize: PropTypes.func,
        handleSort: PropTypes.func,
        handlePaginationVisible: PropTypes.func,
        // table
        refresh: PropTypes.func,
        meta: PropTypes.object,
        items: PropTypes.array,
        settings: PropTypes.object,
        sort: PropTypes.object,
        pagination: PropTypes.object,
        showTableLabel: PropTypes.bool,
        hideGroups: PropTypes.bool,
        // wrappers
        height: PropTypes.number,
        rowClasses: PropTypes.array.isRequired,
        rowKeys: PropTypes.array.isRequired
    };

    render() {
        const { refresh, defaultColumns, settings = { fields: [] }, items = [], meta, height, showTableLabel } = this.props;

        const locked = getLockedCount(Number(settings.locked) || 0, settings.fields);
        const columns = getTableColumns(defaultColumns, settings.fields);
        const lockedColumns = !isEmpty(settings.fields) ? (locked ? locked + 2 : 1) : 0;
        const maxLines = propOr('nolimit', 'stringHeight', settings);

        const classes = cx('react-table', 'react-table-row-lines-' + maxLines);
        const style = {
            width: '100%',
            height: height + 'px'
        };

        return (
            <div ref={node => this.container = node} className={classes} style={style}>
                { meta.error && isEmpty(items) && (
                    <div className="table-error">
                        <ErrorHTML meta={meta} refresh={refresh} />
                    </div>
                )}
                <StickyTable onScroll={this.handleScroll} stickyColumnsCount={lockedColumns}>
                    { this.renderTableHeader(columns) }

                    { showTableLabel && (
                        <TableLabelWithRoute columns={columns} className="table-row-group-skyblue" />
                    )}

                    { meta.success && isEmpty(items) && this.renderEmptyContent(columns) }
                    { !isEmpty(items) && this.renderTableContent(items, columns, maxLines) }
                </StickyTable>
            </div>
        );
    }

    renderTableHeader(columns) {
        const { sort, handleColumnResize, handleSort } = this.props;

        return columns ? (
            <Row>
                { mapIndexed((column, colIndex) => {
                    const translation = this.getTranslation(column.name);
                    const widthStyle = {
                        width: column.width || DEFAULT_COLUMN_WIDTH
                    };

                    return (
                        <Cell key={colIndex} style={widthStyle}>
                            <TableHeaderCell
                                column={column}
                                sort={sort}
                                widthStyle={widthStyle}
                                handleColumnResize={handleColumnResize}
                                handleSort={handleSort}
                                translation={translation}
                            />
                        </Cell>
                    );
                }, columns)}
            </Row>
        ) : null;
    }

    renderTableContent(items = [], columns = [], maxLines) {
        const { cellHandlers, getTemplate } = this.props;

        return mapIndexed((item, rowIndex) => (
            <Row key={this.getRowKey(rowIndex)} className={this.getRowClass(rowIndex)}>
                { mapIndexed((column, key) => (
                    <Cell key={key}>
                        <div style={{width: column.width || DEFAULT_COLUMN_WIDTH}}
                             className={ column.additionalClass }>
                            <TableContentCell
                                {...this.getCellData(column, item, key)}
                                rowIndex={rowIndex}
                                item={item}
                                getTemplate={getTemplate}
                                handlers={cellHandlers}
                                maxLines={maxLines}
                            />
                        </div>
                    </Cell>
                ), columns)}
            </Row>
        ), items);
    }

    renderEmptyContent(columns) {
        return TableLabel({
            columns,
            children: <Translate content="common.no_data" />
        });
    }

    handleScroll = ({ scrollLeft, scrollTop = 0, scrollHeight = 0, clientHeight = 0 }) => {
        const isScrollEnd = scrollHeight - clientHeight - scrollTop < 20;

        this.props.handlePaginationVisible(isScrollEnd);

        if (this.container) {
            this.container.classList.toggle('react-table-scrolled-y', scrollTop);
            this.container.classList.toggle('react-table-scrolled-x', scrollLeft);
        }
    }

    getRowClass = (rowIndex) => {
        return this.props.rowClasses[rowIndex];
    }

    getRowKey = (rowIndex) => {
        return this.props.rowKeys[rowIndex];
    }

    getCellData = (column, item, columnIndex) => {
        let columnKey, columnName;

        if (item.groupType) {
            columnKey = columnName = columnIndex ? null : item.groupType;
        } else {
            columnName = column.name;
            columnKey = column.keyName || column.name;
        }

        return {
            columnName,
            value: item[columnKey],
            translation: this.getTranslation(column.name)
        };
    }

    getTranslation = (columnName) => {
        return this.props.headerTranslatePrefix + columnName;
    }
}
